/**
 * DateInput.js — Custom date input showing dd/mm/yyyy format
 *
 * WHY: Browser's native type="date" shows mm/dd/yyyy on Windows (US locale).
 * This component renders a styled text input with dd/mm/yyyy placeholder,
 * parses user input, and calls onChange with yyyy-mm-dd value for backend.
 *
 * PROPS:
 *   value       — string in "yyyy-mm-dd" format (for controlled input)
 *   onChange    — called with synthetic event: { target: { value: "yyyy-mm-dd" } }
 *   placeholder — default "dd/mm/yyyy"
 *   style       — optional inline styles
 *   min         — "yyyy-mm-dd" min date (optional)
 *   max         — "yyyy-mm-dd" max date (optional)
 *   disabled    — boolean
 *   name        — input name attribute
 *   id          — input id attribute
 *   className   — optional CSS class
 *   onFocus     — optional focus handler
 *   onBlur      — optional blur handler
 */

import React, { useState, useEffect, useRef } from 'react';

/* Convert yyyy-mm-dd → dd/mm/yyyy for display */
const toDisplay = (iso) => {
  if (!iso || typeof iso !== 'string') return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [yyyy, mm, dd] = parts;
  if (!yyyy || !mm || !dd) return '';
  return `${dd}/${mm}/${yyyy}`;
};

/* Convert dd/mm/yyyy → yyyy-mm-dd for backend */
const toISO = (display) => {
  if (!display || typeof display !== 'string') return '';
  // Support both dd/mm/yyyy and dd-mm-yyyy
  const clean = display.replace(/-/g, '/');
  const parts = clean.split('/');
  if (parts.length !== 3) return '';
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return '';
  if (yyyy.length !== 4 || mm.length < 1 || dd.length < 1) return '';
  const d = parseInt(dd, 10);
  const m = parseInt(mm, 10);
  const y = parseInt(yyyy, 10);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return '';
  if (m < 1 || m > 12 || d < 1 || d > 31) return '';
  return `${yyyy}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
};

/* Auto-insert slashes as user types */
const autoFormat = (raw) => {
  // Strip non-digits
  const digits = raw.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}/${digits.slice(2)}`;
  return `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4,8)}`;
};

/* Validate a dd/mm/yyyy string */
const isValidDate = (display) => {
  const iso = toISO(display);
  if (!iso) return false;
  const d = new Date(iso);
  return d instanceof Date && !isNaN(d);
};

export default function DateInput({
  value = '',
  onChange,
  placeholder = 'dd/mm/yyyy',
  style = {},
  min,
  max,
  disabled = false,
  name,
  id,
  className = '',
  onFocus,
  onBlur,
}) {
  const [displayVal, setDisplayVal] = useState(toDisplay(value));
  const [hasError,   setHasError]   = useState(false);
  const prevValue = useRef(value);

  /* Sync when external value changes */
  useEffect(() => {
    if (value !== prevValue.current) {
      prevValue.current = value;
      setDisplayVal(toDisplay(value));
      setHasError(false);
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;

    // Allow clearing
    if (!raw) {
      setDisplayVal('');
      setHasError(false);
      if (onChange) onChange({ target: { name, value: '' } });
      return;
    }

    // Auto-format with slashes
    const formatted = autoFormat(raw);
    setDisplayVal(formatted);

    // Only call onChange when we have a complete valid date (dd/mm/yyyy = 10 chars)
    if (formatted.length === 10) {
      const iso = toISO(formatted);
      if (iso) {
        // Check min/max
        let inRange = true;
        if (min && iso < min) inRange = false;
        if (max && iso > max) inRange = false;

        if (inRange) {
          setHasError(false);
          if (onChange) onChange({ target: { name, value: iso } });
        } else {
          setHasError(true);
        }
      } else {
        setHasError(true);
      }
    } else {
      setHasError(false);
    }
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, arrows, home, end
    const allowed = ['Backspace','Delete','Tab','Escape','ArrowLeft','ArrowRight','Home','End'];
    if (allowed.includes(e.key)) return;
    // Allow: digits only
    if (!/^\d$/.test(e.key) && e.key !== '/') {
      e.preventDefault();
    }
  };

  const handleBlur = (e) => {
    // On blur, validate and show error if incomplete
    if (displayVal && displayVal.length > 0 && displayVal.length < 10) {
      setHasError(true);
    } else if (displayVal.length === 10 && !isValidDate(displayVal)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
    if (onBlur) onBlur(e);
  };

  const handleFocus = (e) => {
    setHasError(false);
    if (onFocus) onFocus(e);
  };

  // Calendar icon click — open a hidden native date input
  const nativeRef = useRef(null);
  const handleCalendarClick = () => {
    if (disabled) return;
    nativeRef.current?.showPicker?.();
    nativeRef.current?.click();
  };

  const handleNativeChange = (e) => {
    const iso = e.target.value; // yyyy-mm-dd
    if (iso) {
      setDisplayVal(toDisplay(iso));
      setHasError(false);
      prevValue.current = iso;
      if (onChange) onChange({ target: { name, value: iso } });
    }
  };

  // Extract width from style prop to apply on wrapper, rest goes to input
  const { width: styleWidth, ...inputStyle } = style;

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: styleWidth || '100%' }}>
      {/* Visible text input — dd/mm/yyyy */}
      <input
        type="text"
        name={name}
        id={id}
        value={displayVal}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={10}
        className={className}
        style={{
          width: '100%',
          paddingRight: 32, // space for calendar icon
          ...inputStyle,
          borderColor: hasError ? '#DC2626' : undefined,
        }}
        autoComplete="off"
      />

      {/* Calendar icon — triggers native picker */}
      <span
        onClick={handleCalendarClick}
        title="Pick date"
        style={{
          position: 'absolute',
          right: 9,
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 0.6,
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#6B7280' }}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </span>

      {/* Hidden native date input for calendar picker */}
      <input
        ref={nativeRef}
        type="date"
        value={value || ''}
        min={min}
        max={max}
        onChange={handleNativeChange}
        style={{
          position: 'absolute',
          opacity: 0,
          width: 1,
          height: 1,
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          zIndex: -1,
        }}
        tabIndex={-1}
      />

      {/* Error hint */}
      {hasError && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 3,
          fontSize: 11,
          color: '#DC2626',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}>
          Valid format: dd/mm/yyyy
        </div>
      )}
    </div>
  );
}
